/**
 * The single definition of "what CSS does a Doc layer have".
 *
 * Two surfaces paint a Doc and they must agree, because one is what the user
 * edits and the other is what ships:
 *
 *   Editor pixels   Doc → layer-renderer.tsx → React → browser
 *   Shipped pixels  Doc → serialize.ts → markup → browser (foreignObject / export)
 *
 * Both used to compute the same declarations independently — two hand-synced
 * implementations of one mapping, which is exactly the arrangement that drifts.
 * They now share this module: it builds a {@link CssMap}, and each caller only
 * chooses an emitter — {@link cssToStyleString} for markup,
 * {@link cssToReactStyle} for React.
 *
 * WRITE ORDER IS THE CASCADE. Declarations come out in the order they were
 * written, and {@link setCss} moves a re-written property to the end, so "last
 * write wins" holds both for the value and for its position. That is what
 * shorthand/longhand pairs depend on — `white-space` before
 * `white-space-collapse`, `border` before a per-side `border-top`, the
 * line-clamp `display` before a hidden layer's `display:none`. The build order
 * below is deliberate:
 *
 *   1. `extraStyles` first, so modeled props always win on conflict
 *   2. box (position / size / transform / opacity / display)
 *   3. paint (fill, border, shadow, radius)
 *   4. typography (text layers only)
 *
 * Editor-only deviations (unclipped glyphs, contentEditable affordances, crop
 * dimming) are layered on top by the app AFTER these declarations, and are
 * documented at their call site. They are deliberate, not drift.
 *
 * The agreement between the two surfaces is enforced by
 * `packages/app/src/pages/ad-studio/html-ad-editor/components/editor-export-parity.test.tsx`
 * — it lives over there because it needs React and a DOM. If you change what
 * this module emits, that is the test that will tell you whether the editor
 * still matches the export. `layer-css.test-part.ts` alongside this file
 * covers the ordering and single-emission invariants.
 */

import { cssPropertyToReactKey } from "./css-property";
import { fontFamilyStackFor } from "./font-family";
import type {
  Border,
  Doc,
  Fill,
  GradientStop,
  ImageCrop,
  ImageLayer,
  Layer,
  RectLayer,
  TextLayer,
  TextShadow,
} from "./model";
import {
  borderRadiusToCss,
  hasRadius,
  isFlexGroup,
  isUniformBorder,
} from "./model";

/**
 * A layer's declarations: kebab-case property → value, in cascade order.
 * One entry per property.
 *
 * Callers receive these ready-built from {@link layerCss} and friends and only
 * pick an emitter; the map is never assembled from outside this module, which
 * is the point — a second place that builds layer CSS is the drift this module
 * exists to prevent.
 */
export type CssMap = Map<string, string>;

/**
 * Write a declaration. Re-writing a property moves it to the END of the map,
 * so its position matches its precedence. Without the delete, a property first
 * written by `extraStyles` would keep that early slot while taking the later
 * value — and any shorthand written in between would then reset it. That is
 * the `white-space` / `white-space-collapse` bug in concrete form.
 *
 * Module-private on purpose: see {@link CssMap}.
 */
function setCss(css: CssMap, property: string, value: string): void {
  css.delete(property);
  css.set(property, value);
}

/**
 * Every declaration for a layer's own element, in cascade order.
 *
 * For a cropped image this is the FRAME element (the clipping box); the inner
 * drawn image is {@link cropImageCss}. For a flex group it is the auto-layout
 * frame. Callers add their own element attributes and any surface-specific
 * overrides on top.
 */
export function layerCss(layer: Layer, inFlow: boolean): CssMap {
  const css: CssMap = new Map();
  // These four calls ARE the cascade documented in the module header. Every
  // layer type runs the same first three; only the paint/typography tail
  // varies. Keep the order.
  applyExtraStyles(css, layer);
  const participatesInFlow = inFlow && !layer.hidden;
  applySizing(css, layer, participatesInFlow);
  applyBoxModifiers(css, layer, participatesInFlow);
  switch (layer.type) {
    case "rect":
      applyPaint(css, layer);
      break;
    case "text":
      applyPaint(css, layer);
      applyTypography(css, layer);
      break;
    case "image":
      applyPaint(css, layer);
      // A crop frame clips; an uncropped image fits itself to its own box.
      if (layer.crop) setCss(css, "overflow", "hidden");
      else setCss(css, "object-fit", layer.objectFit);
      break;
    case "svg":
    case "group":
      break;
  }
  return css;
}

/**
 * The image drawn inside a crop frame. The frame is the visible window; the
 * image keeps its own aspect-ratio-locked box and is offset within it, which
 * is what stops a crop from distorting. The image keeps its original
 * `object-fit`; `contain` is significant when an authored clipping frame trims
 * transparent padding around a logo. `max-width`/`max-height` are pinned to
 * `none` because host stylesheets (the app's CSS-Remedy reset, email clients)
 * routinely clamp images to their container and would shift the crop.
 */
export function cropImageCss(
  crop: ImageCrop,
  objectFit: ImageLayer["objectFit"],
): CssMap {
  return new Map([
    ["position", "absolute"],
    ["left", px(crop.x)],
    ["top", px(crop.y)],
    ["width", px(crop.width)],
    ["height", px(crop.height)],
    ["max-width", "none"],
    ["max-height", "none"],
    ["object-fit", objectFit],
  ]);
}

/**
 * The doc root element. `size` overrides the painted area without changing the
 * declared doc dimensions — the editor grows it so a layer dragged past the
 * edge isn't clipped. Serializers and exports omit it.
 */
export function docRootCss(
  doc: Doc,
  size?: { width: number; height: number },
): CssMap {
  const css: CssMap = new Map([
    ["position", "relative"],
    ["width", px(size?.width ?? doc.width)],
    ["height", px(size?.height ?? doc.height)],
    ["overflow", "hidden"],
  ]);
  if (doc.background) setCss(css, "background", doc.background);
  return css;
}

// ── Emitters ────────────────────────────────────────────────────────────────

/** Declarations as an inline-style string: `a:1;b:2`. No trailing semicolon. */
export function cssToStyleString(css: CssMap): string {
  const out: string[] = [];
  for (const [property, value] of css) out.push(`${property}:${value}`);
  return out.join(";");
}

/**
 * Declarations as a React style object. Insertion order is preserved, which is
 * what keeps the cascade above intact — React writes the properties onto the
 * element in object order.
 */
export function cssToReactStyle(css: CssMap): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [property, value] of css) {
    out[cssPropertyToReactKey(property)] = value;
  }
  return out;
}

// ── Box ─────────────────────────────────────────────────────────────────────

function applyExtraStyles(css: CssMap, layer: Layer): void {
  for (const [property, value] of Object.entries(layer.extraStyles ?? {})) {
    setCss(css, property, value);
  }
}

/**
 * Position and size. Which axes get pinned is the only thing that varies by
 * layer type: text follows its resizing mode, a flex frame hugs its height
 * unless the user pinned one, everything else pins both.
 */
function applySizing(css: CssMap, layer: Layer, inFlow: boolean): void {
  if (layer.type === "text") {
    applyTextBox(css, layer, inFlow);
    return;
  }
  if (layer.type === "group" && isFlexGroup(layer)) {
    applyFlexFrame(css, layer, inFlow);
    return;
  }
  applyAnchor(css, layer, inFlow);
  setCss(css, "width", px(layer.width));
  setCss(css, "height", px(layer.height));
}

/**
 * Absolute placement, unless the layer is a child of a flex auto-layout group
 * — then the flex container owns placement and no anchor is emitted.
 */
function applyAnchor(css: CssMap, layer: Layer, inFlow: boolean): void {
  if (inFlow) {
    if (layer.type === "group") setCss(css, "position", "relative");
    return;
  }
  setCss(css, "position", "absolute");
  setCss(css, "left", px(layer.x));
  setCss(css, "top", px(layer.y));
}

/**
 * Transform, opacity and the hidden flag — the tail of every layer's box.
 *
 * Text is the only layer type with grow anchors, and they compose with
 * rotation and flips into one `transform`. In-flow layers get no anchor: the
 * flex container owns placement.
 *
 * The hidden flag lands here, AFTER {@link applyTextBox} may have written the
 * clamp's `display:-webkit-box`, so a hidden clamped text layer stays hidden.
 *
 * ROUND BEFORE TESTING THE THRESHOLD. Both `rotation` and `opacity` are
 * omitted when they carry no visual effect, and the test has to run on the
 * value we would actually emit — otherwise rounding pushes a value across the
 * boundary and serialize stops being idempotent: `opacity:0.9996` emits
 * `opacity:1`, which the next parse reads back as exactly 1 and then omits, so
 * one Doc yields two different strings on successive saves.
 */
function applyBoxModifiers(css: CssMap, layer: Layer, inFlow: boolean): void {
  const transforms = [anchorTransform(layer, inFlow), layerTransform(layer)]
    .filter((part): part is string => part !== undefined)
    .join(" ");
  if (transforms) setCss(css, "transform", transforms);
  const opacity = roundOpacity(layer.opacity);
  if (opacity < 1) setCss(css, "opacity", `${opacity}`);
  if (layer.hidden) setCss(css, "display", "none");
}

/**
 * How far a text layer's box sits back from its `x`/`y`, as a fraction of its
 * own size: 0.5 for a center anchor, 1 for right/bottom. The layer's visual
 * box is `x - anchor.x * width`, `y - anchor.y * height`. Zero for every
 * other layer, and for in-flow text, whose container owns placement.
 */
export function textAnchorFractions(
  layer: Layer,
  inFlow = false,
): { x: number; y: number } {
  if (inFlow || layer.type !== "text") return { x: 0, y: 0 };
  return {
    x: layer.anchorX === "center" ? 0.5 : layer.anchorX === "right" ? 1 : 0,
    y: layer.anchorY === "middle" ? 0.5 : layer.anchorY === "bottom" ? 1 : 0,
  };
}

/**
 * The translate that realizes a text layer's grow anchors, or undefined when
 * it has none. It is the outermost part of the layer's transform: anything
 * that animates the layer (a scale or rotate block) must sit inside it, so the
 * pivot is the visible box and not the box before the anchor shifted it.
 */
export function anchorTransform(
  layer: Layer,
  inFlow: boolean,
): string | undefined {
  const anchor = textAnchorFractions(layer, inFlow);
  const parts: string[] = [];
  if (anchor.x) parts.push(`translateX(-${anchor.x * 100}%)`);
  if (anchor.y) parts.push(`translateY(-${anchor.y * 100}%)`);
  return parts.length ? parts.join(" ") : undefined;
}

/** The parser stores only a positive uniform scale here; rotation/flips are modeled separately. */
export function layerScale(layer: Layer): number {
  const value = /^scale\(([^)]+)\)$/.exec(
    layer.extraStyles?.transform ?? "",
  )?.[1];
  const scale = value === undefined ? 1 : Number(value);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

/**
 * The layer's own rotation, flips and uniform scale, or undefined at rest.
 * Flips mirror in the rotated space. The innermost part of the layer's transform.
 */
export function layerTransform(layer: Layer): string | undefined {
  const out: string[] = [];
  const rotation = layer.rotation ? roundDeg(layer.rotation) : 0;
  if (rotation !== 0) out.push(`rotate(${rotation}deg)`);
  if (layer.flipX) out.push("scaleX(-1)");
  if (layer.flipY) out.push("scaleY(-1)");
  // Uniform scales survive ungrouping on every child type.
  if (layer.extraStyles?.transform) {
    out.push(layer.extraStyles.transform);
  }
  return out.length ? out.join(" ") : undefined;
}

/**
 * An auto-layout frame: anchored (unless itself in flow) with a pinned width;
 * height hugs content unless the user pinned it (non-zero model height).
 * `display:flex` and the flex properties ride in via `extraStyles`.
 */
function applyFlexFrame(css: CssMap, layer: Layer, inFlow: boolean): void {
  applyAnchor(css, layer, inFlow);
  setCss(css, "width", px(layer.width));
  if (layer.height > 0) setCss(css, "height", px(layer.height));
}

/**
 * A text layer's box. Unlike other layers, the pinned axes depend on the
 * resizing mode: `auto-width` hugs both axes and forces one line;
 * `auto-height` pins width only and carries the line-clamp kit.
 */
function applyTextBox(css: CssMap, layer: TextLayer, inFlow: boolean): void {
  applyAnchor(css, layer, inFlow);
  if (layer.resizing === "auto-width") {
    setCss(css, "width", "max-content");
    // The `white-space` shorthand resets `white-space-collapse` to `collapse`,
    // wiping a `preserve` riding through extraStyles and fusing authored line
    // breaks into one line. Re-apply the longhand after the shorthand — setCss
    // moves it past the shorthand even though extraStyles wrote it first.
    setCss(css, "white-space", "nowrap");
    const collapse = layer.extraStyles?.["white-space-collapse"];
    if (collapse) setCss(css, "white-space-collapse", collapse);
    return;
  }
  setCss(css, "width", px(layer.width));
  // Wrapping modes carry the editor's Figma-style wrap semantic into the
  // markup itself: the canvas renders text with `word-break: break-word`
  // (TextView), so launch and previews must break the same way — a user who
  // shrinks a box below a word's width sees the mid-word wrap everywhere, not
  // just in the editor. `parse.ts` drops the property on read (it's
  // model-owned), so it never duplicates through `extraStyles`.
  setCss(css, "word-break", "break-word");
  if (layer.resizing === "auto-height") {
    if (layer.maxLines) {
      setCss(css, "display", "-webkit-box");
      setCss(css, "-webkit-box-orient", "vertical");
      setCss(css, "-webkit-line-clamp", `${layer.maxLines}`);
    }
    return;
  }
  setCss(css, "height", px(layer.height));
  if (layer.verticalAlign) {
    const alignment = { top: "start", center: "center", bottom: "end" };
    setCss(css, "align-content", alignment[layer.verticalAlign]);
  }
}

// ── Paint ───────────────────────────────────────────────────────────────────

function applyPaint(
  css: CssMap,
  layer: RectLayer | TextLayer | ImageLayer,
): void {
  if (layer.fill) applyFill(css, layer.fill);
  if (layer.border) applyBorder(css, layer.border);
  if (layer.shadow.length > 0) {
    setCss(
      css,
      "box-shadow",
      layer.shadow
        .map(
          (s) =>
            `${px(s.x)} ${px(s.y)} ${px(s.blur)} ${px(s.spread)} ${s.color}`,
        )
        .join(", "),
    );
  }
  if (hasRadius(layer.borderRadius)) {
    setCss(css, "border-radius", borderRadiusToCss(layer.borderRadius));
  }
}

function applyFill(css: CssMap, fill: Fill): void {
  setCss(
    css,
    fill.kind === "solid" ? "background-color" : "background-image",
    fillBackgroundValue(fill),
  );
}

/** A fill as a single CSS background value: the color of a solid fill, or the gradient image. */
export function fillBackgroundValue(fill: Fill): string {
  if (fill.kind === "solid") return fill.color;
  if (fill.kind === "linear") {
    return `linear-gradient(${roundDeg(fill.angle)}deg,${formatStops(fill.stops)})`;
  }
  // Omit the shape keyword — CSS defaults to `ellipse at center`, which
  // matches what the source typically had. Hardcoding `circle` makes
  // non-square containers render the gradient as a circle (visibly wrong).
  return `radial-gradient(${formatStops(fill.stops)})`;
}

function formatStops(stops: GradientStop[]): string {
  return stops
    .map((stop) => `${stop.color} ${Math.round(stop.offset)}%`)
    .join(",");
}

function applyBorder(css: CssMap, border: Border): void {
  if (isUniformBorder(border)) {
    setCss(css, "border", `${px(border.top.width)} solid ${border.top.color}`);
    return;
  }
  // Per-side mode (e.g. CSS-triangle). Never mix with the uniform shorthand —
  // a `border:` shorthand in the output would reset all four sides regardless
  // of declaration order.
  const sides = [
    ["top", border.top],
    ["right", border.right],
    ["bottom", border.bottom],
    ["left", border.left],
  ] as const;
  for (const [name, side] of sides) {
    if (side.width > 0) {
      setCss(css, `border-${name}`, `${px(side.width)} solid ${side.color}`);
    }
  }
}

// ── Typography ──────────────────────────────────────────────────────────────

function applyTypography(css: CssMap, layer: TextLayer): void {
  setCss(css, "font-family", fontFamilyStackFor(layer.fontFamily));
  setCss(css, "font-size", px(layer.fontSize));
  setCss(css, "font-weight", `${layer.fontWeight}`);
  setCss(css, "color", layer.color);
  setCss(css, "text-align", layer.textAlign);
  if (layer.lineHeight) setCss(css, "line-height", layer.lineHeight);
  if (layer.letterSpacing) setCss(css, "letter-spacing", layer.letterSpacing);
  if (layer.italic) setCss(css, "font-style", "italic");
  const decoration = [
    layer.underline && "underline",
    layer.strikethrough && "line-through",
  ]
    .filter(Boolean)
    .join(" ");
  if (decoration) setCss(css, "text-decoration", decoration);
  if (layer.textShadow.length > 0) {
    setCss(css, "text-shadow", textShadowCss(layer.textShadow));
  }
}

function textShadowCss(shadows: TextShadow[]): string {
  return shadows
    .map(
      (shadow) =>
        `${px(shadow.x)} ${px(shadow.y)} ${px(shadow.blur)} ${shadow.color}`,
    )
    .join(", ");
}

// ── Number formatting ───────────────────────────────────────────────────────
// Rounding is part of the contract: both surfaces must round identically or
// they diverge by sub-pixel amounts that accumulate through parse/serialize
// round-trips.

function px(value: number): string {
  return `${Math.round(value * 100) / 100}px`;
}

function roundDeg(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundOpacity(value: number): number {
  return Math.round(value * 1000) / 1000;
}
